// apps/share-web/src/offline/idb-backend.ts
//
// Единственный модуль волны, который трогает IndexedDB. Ровно четыре обёртки
// над KvBackend и ни одного решения: любая логика, попавшая сюда, окажется
// непокрытой — в тестовом окружении IndexedDB нет (см. kv-backend.ts).
// Долг: TD-260921-idb-adapter-untested. Приёмка — живая, на телефоне.
//
// Одна объектная витрина `kv` с ключом-строкой. Номер версии заведён с
// первого коммита: без него переход на новую форму записи пришлось бы делать
// стиранием, то есть терять офлайн ровно тогда, когда человек обновил
// приложение перед служением. (Форма самой ЗАПИСИ версионируется отдельно,
// внутри значения — см. `SHAPE` в offline-store.ts.)
//
// Три способа не открыться, и все три обязаны кончаться мягким отказом, а не
// зависанием: зависший промис хуже отказа — вьюха ждёт его вечно и офлайн не
// наступает никогда.
//   1. `factory.open` бросает СИНХРОННО (хранилище заблокировано политикой
//      браузера) — тогда бэкенда нет вовсе, возвращаем null;
//   2. открытие отвечает ошибкой — бэкенд есть, но «недоступен»: каждая
//      операция мягко отказывает;
//   3. открытие БЛОКИРУЕТСЯ другой вкладкой (`onblocked`) — случится в день,
//      когда поднимут DB_VERSION. Без обработчика промис не резолвится и не
//      реджектится НИКОГДА.
// Отдельно: `.catch()` вешается на промис открытия СРАЗУ, в той же строке,
// где он создаётся. Отложить до первой операции значило бы unhandled
// rejection на буте каждого такого телефона.

import type { KvBackend } from './kv-backend.js';

const DB_NAME = 'bandway-offline';
const DB_VERSION = 1;
const STORE = 'kv';

function awaitOpen(req: IDBOpenDBRequest): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => {
      const db = req.result;
      // Другая вкладка поднимает версию — эта обязана отпустить базу, иначе
      // она сама станет тем `onblocked`, от которого страдает соседняя.
      db.onversionchange = () => db.close();
      resolve(db);
    };
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('idb-backend: open blocked by another tab'));
  });
}

function wrap<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** null, если IndexedDB здесь нет (private mode, старый WebView, тесты).
 *  Потребитель обязан пережить null: офлайн — усиление, а не несущая часть. */
export function idbBackend(factory?: IDBFactory): KvBackend | null {
  const f = factory ?? (typeof indexedDB === 'undefined' ? null : indexedDB);
  if (!f) return null;

  let req: IDBOpenDBRequest;
  try {
    req = f.open(DB_NAME, DB_VERSION);
  } catch {
    return null;
  }

  // `.catch` здесь же: промис открытия никем не ожидается до первой операции.
  // `null` внутри промиса и есть состояние «бэкенд недоступен».
  const dbp: Promise<IDBDatabase | null> = awaitOpen(req).catch(() => null);

  const tx = async <T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T | null> => {
    const db = await dbp;
    if (!db) return null;
    return wrap(fn(db.transaction(STORE, mode).objectStore(STORE)));
  };

  return {
    async get(key) { return (await tx('readonly', (s) => s.get(key))) ?? null; },
    async set(key, value) { await tx('readwrite', (s) => s.put(value, key)); },
    async keys(prefix) {
      const all = (await tx('readonly', (s) => s.getAllKeys())) as IDBValidKey[] | null;
      if (!all) return [];
      const strs = all.filter((k): k is string => typeof k === 'string');
      return prefix ? strs.filter((k) => k.startsWith(prefix)) : strs;
    },
    async del(keys) {
      // ОДНА транзакция на весь список: сверка после отзыва доступа сносит
      // десятки ключей разом, и транзакция на ключ превращала бы это в
      // десятки round-trip'ов по диску телефона.
      if (!keys.length) return;
      const db = await dbp;
      if (!db) return;
      const t = db.transaction(STORE, 'readwrite');
      const s = t.objectStore(STORE);
      for (const k of keys) s.delete(k);
      await new Promise<void>((resolve, reject) => {
        t.oncomplete = () => resolve();
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error);
      });
    },
  };
}
